<template>
    <v-dialog v-model="projectStatusDialog" width="400">
        <v-card component="ProjectStatusDialog">
            <btn class="close" size="icon" color="transparent" :icon="'close'" @click="setProjectStatusDialog(false)" />

            <section class="create">
                <h2>Basic information</h2>
                <p>Created on: {{ getDateWithTime(project.zaciatok) }}</p>
                <p>
                    Created by: <a :href="`mailto:${project.vytvoril}`">{{ project.vytvoril }}</a>
                </p>
                <p>Available until: {{ getDateWithTime(project.zobrazit_do) }}</p>
                <v-spacer />
                <p>Piece type: {{ getProperty('types', project.detail.typ) }}</p>
                <p>Length in seconds: {{ getProperty('seconds', project.detail.dlzka) }}</p>
                <p>Territory: {{ getProperty('territories', project.detail.teritory) }}</p>
                <p>Timespan: {{ getProperty('timespans', project.detail.obdobie) }}</p>
            </section>

            <div class="radio-input">
                <label class="radio">
                    <input v-model="project_type" name="project-type" type="radio" value="voice" />
                    <span></span>
                    VOICE
                </label>
                <label class="radio">
                    <input v-model="project_type" name="project-type" type="radio" value="face" />
                    <span></span>
                    FACE
                </label>
            </div>

            <hr />

            <section class="send">
                <h2>Sent for approval</h2>
                <div class="holder" v-if="projectWasSent">
                    <p>Sent on: {{ getDateWithTime(project.schvalenie_odoslane) }}</p>
                    <p>
                        Sent by: <a :href="`mailto:${project.odoslal}`">{{ project.odoslal }}</a>
                    </p>
                    <p>
                        On email: <a :href="`mailto:${project.schvalenie_email}`">{{ project.schvalenie_email }}</a>
                    </p>
                    <btn
                        class="send"
                        :state="!projectHasCharacter ? 'disabled' : ''"
                        v-if="!projectWasApproved"
                        size="small"
                        color="base"
                        :icon="'send'"
                        :text="'Resend'"
                        @click="send"
                    />
                </div>
                <div v-else>
                    <p>Project was not sent.</p>
                    <btn
                        class="send"
                        :state="!projectHasCharacter ? 'disabled' : ''"
                        size="small"
                        color="base"
                        :icon="'send'"
                        :text="'Send now'"
                        @click="send"
                    />
                </div>
            </section>

            <hr />

            <section class="approve">
                <h2>Approval</h2>
                <div class="holder" v-if="projectWasApproved">
                    <p>Approved on: {{ getDateWithTime(project.datum_schvalenia) }}</p>
                    <p>
                        Approved by: <a :href="`mailto:${project.schvalil}`">{{ project.schvalil }}</a>
                    </p>
                </div>
                <div v-else>
                    <p>Project was not approved.</p>
                    <btn
                        class="approve"
                        :state="!projectHasCharacter ? 'disabled' : ''"
                        size="small"
                        color="accent"
                        :icon="'check'"
                        :text="'Approve now'"
                        @click="approve"
                    />
                </div>
            </section>

            <hr />

            <section class="order">
                <h2>Order</h2>
                <div class="holder" v-if="projectWasClosed">
                    <p>Created on: {{ getDateWithTime(project.order.vytvorena) }}</p>
                </div>
                <div v-else>
                    <p>Project was not ordered.</p>
                    <btn
                        class="order"
                        :state="canCloseProject ? '' : 'disabled'"
                        size="small"
                        color="primary"
                        :icon="'shopping_cart'"
                        :text="'Order now'"
                        @click="order"
                    />
                </div>
            </section>

            <hr />

            <section class="prolong">
                <h2>Prolongation</h2>
                <div class="holder" v-if="projectWasProlonged">
                    <p v-for="date in project.prolongs" :key="`prolongs-${date.id}`">
                        Prolonged on: {{ getDateWithTime(date.created_at) }}
                    </p>
                    <btn
                        v-if="!projectWasClosed"
                        class="prolong"
                        :state="canProlongProject ? '' : 'disabled'"
                        size="small"
                        color="secondary"
                        :icon="'restore'"
                        :text="'Prolong'"
                        @click="prolong"
                    />
                </div>
                <div v-else>
                    <p>Project was not prolonged.</p>
                    <btn
                        v-if="!projectWasClosed"
                        class="prolong"
                        :state="canProlongProject ? '' : 'disabled'"
                        size="small"
                        color="secondary"
                        :icon="'restore'"
                        :text="'Prolong'"
                        @click="prolong"
                    />
                </div>
            </section>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import moment from 'moment';

export default {
    name: 'ProjectStatusDialog',

    props: {
        project: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            project_type: 'voice',
        };
    },

    methods: {
        ...mapActions([]),
        ...mapMutations([
            'setProjectStatusDialog',
            'setProjectSendDialog',
            'setProjectApproveDialog',
            'setProjectProlongDialog',
            'setProjectVoiceFaceType',
        ]),

        getDateWithTime(date) {
            return `${moment(date).format('DD. MM. YYYY')} at ${moment(date).format('HH:mm')}`;
        },

        send() {
            this.setProjectVoiceFaceType(this.project_type);
            this.setProjectStatusDialog(false);
            this.setProjectSendDialog(true);
        },

        approve() {
            this.setProjectStatusDialog(false);
            this.setProjectApproveDialog(true);
        },

        order() {
            this.setProjectStatusDialog(false);
            this.$router.push({ name: 'project-order', params: { project_id: this.project.id } });
        },

        prolong() {
            this.setProjectStatusDialog(false);
            this.setProjectProlongDialog(true);
        },

        getProperty(property, id) {
            return this.properties[property].find((item) => item.value === id).name;
        },
    },

    computed: {
        ...mapGetters([
            'getProjectStatusDialog',
            'projectWasSent',
            'projectWasApproved',
            'projectWasProlonged',
            'projectWasClosed',
            'canCloseProject',
            'canProlongProject',
            'getProjectFormAttributes',
            'projectHasCharacter',
        ]),

        projectStatusDialog: {
            get: function () {
                return this.getProjectStatusDialog;
            },
            set: function (val) {
                this.setProjectStatusDialog(val);
            },
        },

        properties() {
            return this.getProjectFormAttributes;
        },
    },

    components: {},
};
</script>

<style lang="less" scoped>
@import '~@variables';

[component='ProjectStatusDialog'] {
    padding: 20px;

    section {
        position: relative;

        button {
            position: absolute;
            right: 0;
            top: 0;
        }
    }

    .close {
        position: absolute;
        top: 0;
        right: 0;
        margin: 0;
    }

    hr {
        padding-bottom: 15px;
        margin-top: 15px;
    }

    h2 {
        margin: 0;
    }

    p {
        margin: 0;
    }

    button {
        margin-top: 5px;

        &.order {
            padding-left: 8px;
        }
    }

    .radio-input {
        display: flex;
        flex-direction: row;
        gap: 20px;
        padding: 10px 0 0 0;

        .radio {
            gap: 10px;

            span {
                margin: 0;
            }
        }
    }
}
</style>
